import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { KeyPair } from '../../../../models/base';
import {
  CompanyEarningConfig,
  EarningCalculations
} from '../../../../models/company-earning-config';
import { JobCode, LaborAllocationTypes } from '../../../../models/labor-allocation';
import {
  AllocatedEarning,
  EmployeePay,
  LaborAllocation,
  Payline,
  PaylineType
} from '../../../../models/payroll';
import { PayrollManagement } from '../../../../tools/payroll-management';
import * as _ from 'underscore';
import { CompaniesService } from '../../../../services/companies.service';

@Component({
  selector: 'lib-labor-allocations',
  templateUrl: './labor-allocations.component.html',
  styleUrls: ['./labor-allocations.component.scss'],
  standalone: false
})
export class LaborAllocationsComponent {
  @ViewChildren('auto') autos;
  @Input() public allocTypeId: number;
  @Input() public clientCode: number;
  @Input() public earningConfigs: Record<string, CompanyEarningConfig>;
  @Input() public employeePay: EmployeePay;
  @Input() public jobCodes = [];
  @Input() public payline: Payline;

  @Output()
  public saveAllocations = new EventEmitter<any>();

  public allocations: AllocationEdit[] = [];
  public codeIndexErrors = {}; // if any code values are invalid or missing
  public codeOptions = [];
  public createCodeView = null;
  public earningsList: CompanyEarningConfig[];
  public formError: string;
  public fullName: string;
  public isFormDirty: boolean = false;
  public isHoursView: boolean;
  public keyword = 'Value';
  public newCode = { Id: null, Value: null };
  public showFormError = false;
  public isReadOnly = false;

  constructor(
    public activeModal: NgbActiveModal,
    public companyService: CompaniesService,
    private payrollMgmt: PayrollManagement
  ) {
    this._onCodeChange = _.debounce(this._onCodeChange, 250);
  }

  get allocTypes() {
    return LaborAllocationTypes;
  }

  ngOnInit() {
    // bug 19460. for clients that allocate by job code, allow a 'None' selection
    if (
      this.allocTypeId == LaborAllocationTypes['Job Code'] &&
      !this.jobCodes.find(x => x.Id == 0)
    ) {
      this.jobCodes.push({ Id: 0, Value: 'None' });
    }

    // task38776 labor alloc modal should be read only for nocheck paylines
    if (this.payline.PaylineType == PaylineType.NoCheck) {
      this.isReadOnly = true;
      this.formError = 'Labor allocation cannot be changed for a No Check payline.';
      this.showFormError = true;
    }

    sessionStorage.setItem('job-code-list', JSON.stringify(this.jobCodes));

    const list = Object.values(this.earningConfigs)
      .sort((a, b) => (a.AppearanceSequence > b.AppearanceSequence ? 1 : -1))
      .filter(x => this.payline.Earnings.find(y => y.EarningId == x.Id));

    this.earningsList = [];
    for (let earn of list) {
      if (!earn.DisplayInGrid) {
        continue;
      }

      // a bit of hackery to ensure that we render out two columns per earning
      // where appropriate. rate/amount and hours.
      if (earn.CanHaveRates || earn.IsPercentageEarning) {
        const e = { ...earn };
        e.CanSetHours = false;
        this.earningsList.push(e);
      }

      if (earn.CanSetHours) {
        const e = { ...earn };
        e.CanHaveRates = false;
        e.IsPercentageEarning = false;
        this.earningsList.push(e);
      }
    }

    this.fullName = this.payrollMgmt.getEmployeeFullName(this.employeePay);

    this._initAllocations();

    const allocs = Object.values(this.payline.Allocations);

    // NOTE: this logic to determine the 'default' state of the labor allocations
    // window is a little messy at this point. The API made a change mid dev cycle
    // to start defaulting allocations to 100% to home department or job code 'None'
    // depending on client configuration. We are now assuming the payline has
    // 'no allocations' if a single 100% record is set to home dept or code 'None'
    if (!this.payrollMgmt.hasLaborAllocations(this.employeePay, this.payline)) {
      this.isHoursView = true;
    } else {
      this.isHoursView = !allocs.find(
        x => x.PermanentPercentageAllocations || x.TemporaryPercentageAllocations
      );
    }

    this._setCodeOptions();
  }

  // #region public methods

  public addAllocation() {
    const totals = this.allocations.map(x => x.Allocations.TemporaryPercentageAllocations);
    const sum = totals.reduce((a, b) => +a + +b, 0);

    const alloc = new LaborAllocation();

    if (sum < 100) {
      alloc.TemporaryPercentageAllocations = 100 - sum;
    }

    this._setDefaultEarningValues(alloc);

    this.allocations.push(new AllocationEdit(null, alloc));

    this.codeIndexErrors[this.allocations.length - 1] = true;
    this.isFormDirty = true;
    this._runSaveValidation();
  }

  public addNewDept(index) {
    this.createCodeView = index;
  }

  public autoCompleteOpened(index) {
    this._updateSuggestionsPosition(index);
  }

  public deleteAllocation(index: number) {
    if (this.codeIndexErrors[index]) {
      delete this.codeIndexErrors[index];
    }

    this.allocations.splice(index, 1);
    this._refreshView();
  }

  public getJobCodeDisplay(id) {
    const code = this.jobCodes.find(x => x.Id == id);
    return code ? code.Value : '';
  }

  public onCancel() {
    sessionStorage.removeItem('job-code-list');
  }

  // job code autocomplete control
  public onCodeChange(allocation: string, index: number) {
    // this function oddly fires when the input is
    // focused manually via the keyboard navigation.
    // check to see if the input was actually changed
    const code = this.jobCodes.find(x => x.Value == allocation);
    if (code && this.allocations[index].Id == code.Id.toString()) {
      return;
    }

    this._onCodeChange(allocation, index);
    this._updateSuggestionsPosition(index);
  }

  public onCodeClear(index) {
    this.allocations[index].Id = null;
    this.codeIndexErrors[index] = true;
    this._refreshView();
    this._updateSuggestionsPosition(index);
  }

  public onCodeFocus(index) {
    this._updateSuggestionsPosition(index);
  }

  public onCodeSelect(code, index) {
    this.allocations[index].Id = code.Id;

    if (this.codeIndexErrors[index]) {
      delete this.codeIndexErrors[index];
    }

    this._refreshView();
  }

  // users want to the ability to navigate around the inputs
  // with the arrow keys.
  // for the autocomplete control, up/down arrow press
  // should not be hijacked as it's used to navigate through
  // results
  public onKeyDown(e: KeyboardEvent, row: number, col: number, autoComplete = false) {
    if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'].includes(e.code)) {
      return;
    }

    if (autoComplete && ['ArrowDown', 'ArrowUp'].includes(e.code)) {
      return;
    }

    this._closeAutoComplete();
    this._onKeyboardNavigate(e.code, row, col);
  }

  public onTableScroll() {
    this._closeAutoComplete();
  }

  public onTempChange() {
    this.isFormDirty = true;
    this._runSaveValidation();
  }

  public onSave(modal) {
    // double check for any errors
    if (Object.keys(this.codeIndexErrors).length) {
      return;
    }

    // if the current view is hours, clear any percentage based edits
    // else clear hours based edits
    const allocs = {};

    for (let alloc of this.allocations) {
      if (alloc.Id == 'None') {
        alloc.Id = '0';
      }

      if (this.isHoursView) {
        alloc.Allocations.PermanentPercentageAllocations =
          alloc.Allocations.TemporaryPercentageAllocations = null;

        // remove any earnings with zero or unset allocation value
        // to avoid null reference exception API errors
        const keys = Object.keys(alloc.Allocations.TemporaryHourlyAllocations);
        for (let key of keys) {
          const val = alloc.Allocations.TemporaryHourlyAllocations[key];
          if (!val) {
            delete alloc.Allocations.TemporaryHourlyAllocations[key];
          } else if (!val.TemporaryRateAmount) {
            val.PermanentRateAmount = val.TemporaryRateAmount = 0;
          } else if (val.TemporaryRateAmount && !val.PermanentRateAmount) {
            // NOTE: in all other places in payroll, the front end never
            // passes permanent rates to the API. for some reason, labor allocations
            // have become the exception there...
            val.PermanentRateAmount = val.TemporaryRateAmount;
          }
        }
      } else {
        alloc.Allocations.TemporaryHourlyAllocations = null;
      }

      allocs[alloc.Id] = alloc.Allocations;
    }

    this.payline.Allocations = allocs;

    this.saveAllocations.emit({ payline: this.payline, personId: this.employeePay.PersonId });
    sessionStorage.removeItem('job-code-list');

    modal.close();
  }

  public saveNewCode() {
    if (!this.newCode.Id || !this.newCode.Value) {
      return;
    }

    const jobCode = new JobCode(this.newCode.Id, this.newCode.Value);
    this.companyService.createJobCode(this.clientCode.toString(), jobCode).subscribe(resp => {
      // NOTE: the response in the create job code is different than GET job codes.
      // In the list of codes, the description will be prepended with "{{code}} -"
      this.jobCodes.push(
        new KeyPair(resp.Model.Id, `${resp.Model.Code} - ${resp.Model.Description}`)
      );
      sessionStorage.setItem('job-code-list', JSON.stringify(this.jobCodes));

      this.allocations[this.createCodeView].Id = resp.Model.Id.toString();

      if (this.codeIndexErrors[this.createCodeView]) {
        delete this.codeIndexErrors[this.createCodeView];
      }

      this._refreshView();

      this.createCodeView = null;
    });
  }

  public selValueRender(value) {
    // render function inside ng-autocomplete
    // "this" changes context to the autocomplete control
    // so access to the component gets tricky
    const codes = sessionStorage.getItem('job-code-list');

    // for clients that alloc by job code,
    // display 'None' in selector by default
    // if no allocations have been set
    if (value == 'None') {
      return value;
    }

    if (!codes) {
      return value;
    }

    const list = JSON.parse(codes);
    return value != null ? list.find(x => x.Id == +value)?.Value : null;
  }

  // bug 19418. when switching views,
  // set a single allocation with default values
  public onViewChange() {
    this.allocations = [];
    this._initDefaultAllocation();
    this._setCodeOptions();
  }

  // #endregion

  // #region private methods

  // autocomplete window is set to fixed position
  // close it when window scrolls, or user uses arrow key navigation
  private _closeAutoComplete() {
    for (let auto of this.autos) {
      auto.close();
    }
  }

  // if no allocations are set, apply 100% to none/home dept
  // else, set temporary allocation defaults if permanent values exist
  private _initAllocations() {
    if (!this.payrollMgmt.hasLaborAllocations(this.employeePay, this.payline)) {
      this._initDefaultAllocation();
      return;
    }

    for (let key of Object.keys(this.payline.Allocations)) {
      const alloc = { ...this.payline.Allocations[key] };

      this._setDefaultEarningValues(alloc);

      // if a permanent value is set and temporary value is null,
      // set temporary value to the permanent value
      if (alloc.PermanentPercentageAllocations && alloc.TemporaryPercentageAllocations == null) {
        alloc.TemporaryPercentageAllocations = alloc.PermanentPercentageAllocations;
      }

      this.allocations.push(new AllocationEdit(key, alloc));
    }
  }

  private _initDefaultAllocation() {
    this.codeIndexErrors = {};
    const alloc = new LaborAllocation();
    alloc.TemporaryPercentageAllocations = 100;

    if (this.allocTypeId == this.allocTypes.Department) {
      // if client allocated by department, set a single allocation record to the home department at 100%
      this.allocations.push(new AllocationEdit(this.employeePay.DepartmentId.toString(), alloc));
    } else if (this.allocTypeId == this.allocTypes['Job Code']) {
      // client allocates by job code, set a single allocation record to 'None' at 100%
      this.allocations.push(new AllocationEdit('0', alloc));
    }

    this._setDefaultEarningValues(alloc);
  }

  private _onCodeChange(allocation: string, index: number) {
    // a selected items was manually changed or cleared
    if (!this.codeIndexErrors[index]) {
      this.codeIndexErrors[index] = true;
      // not using 2 way model binding on the input
      // set this value so the suggestions update correctly
      this.allocations[index].Id = allocation;
      this._refreshView();
    }
  }

  private _onKeyboardNavigate(keyCode: string, row: number, column: number) {
    let direction: 'up' | 'down' | 'left' | 'right';
    if (keyCode == 'ArrowLeft') {
      direction = 'left';
    } else if (keyCode == 'ArrowRight') {
      direction = 'right';
    } else if (keyCode == 'ArrowUp') {
      direction = 'up';
    } else if (['ArrowDown', 'NumpadEnter', 'Enter'].includes(keyCode)) {
      direction = 'down';
    }

    let next = this._stepToNextInput(direction, row, column);
    row = next.row;
    column = next.column;

    let nextInput = document.getElementById(`column-${column}-row-${row}`);

    if (!nextInput) {
      return;
    }

    if (nextInput.classList.contains('ng-autocomplete')) {
      this.autos._results[row - 1].focus();
    } else {
      nextInput.focus();
    }
  }

  private _refreshView() {
    this.isFormDirty = true;
    this._setCodeOptions();
    this._runSaveValidation();
  }

  // disable the save button if
  // no changes have been made
  // any codes are invalid, missing
  // view is 'percent' and sum is not 100%
  private _runSaveValidation() {
    if (!this.isFormDirty) {
      // no changes have been made
      this.formError = null;
    } else if (Object.keys(this.codeIndexErrors).length) {
      this.formError = `*one or more invalid ${this.allocTypes[this.allocTypeId]}s has been set`;
    } else {
      const totals = this.allocations.map(x => x.Allocations.TemporaryPercentageAllocations);
      const sum = totals.reduce((a, b) => +a + +b, 0);
      if (!this.isHoursView && sum != 100) {
        this.formError = '*percentages must add up to 100%';
      } else {
        this.formError = null;
      }
    }
  }

  private _setCodeOptions() {
    const keys = this.allocations.map(x => (x.Id ? x.Id.toString() : null));
    const options = this.jobCodes.filter(x => !keys.includes(x.Id.toString()));
    this.codeOptions = options && options.length ? options : null;
  }

  // when allocation type is wages/hours -
  // individual dollar amount or hours will be entered per earning code.
  // *) Rates for rated earnings default to the rate on the earning for the pay date of the payroll.
  // *) Fixed dollar amounts default to the amount on the earning for the pay date of the payroll.
  // *) Hours default to zero.
  private _setDefaultEarningValues(alloc: LaborAllocation) {
    if (!alloc.TemporaryHourlyAllocations) {
      alloc.TemporaryHourlyAllocations = {};
    }

    for (let id of Object.keys(this.earningConfigs)) {
      const config = this.earningConfigs[id];

      if (!config.DisplayInGrid) {
        continue;
      }

      const a = alloc.TemporaryHourlyAllocations[config.Id];

      // check to see if there is an allocation record
      // if not, default earning values will be set below
      if (a != null && a != undefined) {
        if (a?.TemporaryRateAmount == null && a?.PermanentRateAmount) {
          // bug 20823 default to the permanent rate amount if one exists
          a.TemporaryRateAmount = a.PermanentRateAmount;
        }

        // there is an allocation record, so stop here
        continue;
      }

      // no allocation value was found,
      // set default values from the Payline's Earnings
      const e = this.payline.Earnings.find(x => x.EarningId == config.Id);

      if (!e) {
        continue;
      }

      let rateAmount = null;
      if (config.CanHaveRates || config.IsPercentageEarning) {
        rateAmount = e.TemporaryRateAmount || e.PermanentRateAmount;
      }

      let hours = null;
      if (config.CanSetHours) {
        hours = e.Hours;
      }

      alloc.TemporaryHourlyAllocations[config.Id] = new AllocatedEarning(rateAmount, hours);
    }
  }

  private _stepToNextInput(
    direction: 'up' | 'down' | 'left' | 'right',
    row: number,
    column: number
  ) {
    if (direction === 'left') {
      column--;
    } else if (direction === 'right') {
      column++;
    } else if (direction === 'up') {
      row--;
    } else if (direction === 'down') {
      row++;
    }

    return { row, column };
  }

  // in order to avoid clipping, the autocomplete results
  // was set to a fixed position. that means the position of
  // the element needs to be set when opened, or the dropdown
  // will progressively get lower on the screen for rows that
  // have been scrolled into view
  private _updateSuggestionsPosition(index) {
    const padding = 2;
    const auto = document.querySelectorAll('ng-autocomplete')[index];
    const top = auto.getBoundingClientRect().bottom + padding + 'px';

    const suggestions = auto.querySelector<HTMLElement>('.suggestions-container');
    if (suggestions) {
      suggestions.style.top = top;
    }

    // not found element isn't inserted into the DOM right away
    setTimeout(() => {
      const notFound = auto.querySelector<HTMLElement>('.not-found');
      if (notFound) {
        notFound.style.top = top;
      }
    });
  }

  //#endregion
}
export class AllocationEdit {
  public Id: string | number;
  public Allocations: LaborAllocation;

  constructor(id: string | number, allocations: LaborAllocation) {
    this.Id = id;
    this.Allocations = allocations;
  }
}
